/**
 * Item Service
 *
 * Handles API calls for Items Repository (Super Admin)
 * Manages Policies, Procedures, Templates, Guidelines, and Resources
 */

import { apiClient } from '@api'

const itemService = {
  /**
   * Get all items with filters and pagination
   * @param {Object} filters - Filter options
   * @param {Object} pagination - Pagination options
   * @returns {Promise} - Items list with pagination
   */
  async getAll(filters = {}, pagination = {}) {
    const params = new URLSearchParams()

    if (filters.search) params.append('search', filters.search)
    if (filters.type && filters.type !== 'all') params.append('type', filters.type)
    if (filters.status && filters.status !== 'all') params.append('status', filters.status)
    if (filters.includeDeleted) params.append('include_deleted', '1')

    params.append('page', pagination.page || 1)
    params.append('limit', pagination.limit || 25)

    const queryString = params.toString()
    return apiClient.get(`/items${queryString ? `?${queryString}` : ''}`)
  },

  /**
   * Get single item by ID
   * @param {number} id - Item ID
   * @returns {Promise} - Item details
   */
  async getById(id) {
    return apiClient.get(`/items/${id}`)
  },

  /**
   * Create new item with file upload
   * @param {FormData} formData - Form data with file
   * @param {Function} onProgress - Progress callback
   * @returns {Promise} - Created item
   */
  async create(formData, onProgress = null) {
    return apiClient.uploadFile('/items', formData.get('file'), {
      title: formData.get('title'),
      description: formData.get('description'),
      item_type: formData.get('item_type'),
      status: formData.get('status'),
    }, onProgress)
  },

  /**
   * Update item (creates new version if file changed)
   * @param {number} id - Item ID
   * @param {FormData} formData - Form data with optional file
   * @param {Function} onProgress - Progress callback
   * @returns {Promise} - Updated item
   */
  async update(id, formData, onProgress = null) {
    const file = formData.get('file')

    if (file && file.size > 0) {
      // Update with new file
      return apiClient.uploadFile(`/items/${id}`, file, {
        title: formData.get('title'),
        description: formData.get('description'),
        item_type: formData.get('item_type'),
        status: formData.get('status'),
        change_reason: formData.get('change_reason'),
        _method: 'PUT',
      }, onProgress)
    } else {
      // Update without file (metadata only)
      return apiClient.put(`/items/${id}`, {
        title: formData.get('title'),
        description: formData.get('description'),
        item_type: formData.get('item_type'),
        status: formData.get('status'),
      })
    }
  },

  /**
   * Delete item (soft delete)
   * @param {number} id - Item ID
   * @returns {Promise}
   */
  async delete(id) {
    return apiClient.delete(`/items/${id}`)
  },

  /**
   * Restore deleted item
   * @param {number} id - Item ID
   * @returns {Promise}
   */
  async restore(id) {
    return apiClient.post(`/items/${id}/restore`)
  },

  /**
   * Get item statistics
   * @returns {Promise} - Statistics data
   */
  async getStats() {
    return apiClient.get('/items/stats')
  },

  /**
   * Get version history for an item
   * @param {number} id - Item ID
   * @returns {Promise} - Version history
   */
  async getVersions(id) {
    return apiClient.get(`/items/${id}/versions`)
  },

  /**
   * Rollback to a previous version
   * @param {number} id - Item ID
   * @param {number} version - Version number to rollback to
   * @returns {Promise}
   */
  async rollback(id, version) {
    return apiClient.post(`/items/${id}/rollback/${version}`)
  },

  /**
   * Get download URL for an item
   * @param {number} id - Item ID
   * @param {number} version - Optional version number
   * @returns {string} - Download URL
   */
  getDownloadUrl(id, version = null) {
    const baseUrl = apiClient.baseURL || import.meta.env.VITE_API_BASE_URL
    let url = `${baseUrl}/items/${id}/download`
    if (version) url += `?version=${version}`
    return url
  },

  /**
   * Download item file
   * @param {number} id - Item ID
   * @param {number} version - Optional version number
   */
  async download(id, version = null) {
    const url = this.getDownloadUrl(id, version)
    const token = sessionStorage.getItem('auth_access_token') || localStorage.getItem('auth_access_token')

    // Create a temporary link with auth token
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      throw new Error('Download failed')
    }

    const blob = await response.blob()
    const contentDisposition = response.headers.get('content-disposition')
    let filename = 'download'

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }

    // Create download link
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  },

  /**
   * Get companies assigned to an item
   * @param {number} id - Item ID
   * @returns {Promise} - Assigned companies
   */
  async getAssignments(id) {
    return apiClient.get(`/items/${id}/assignments`)
  },

  /**
   * Assign item to companies
   * @param {number} id - Item ID
   * @param {number[]} companyIds - Array of company IDs
   * @param {number} version - Optional version to assign
   * @returns {Promise}
   */
  async assignToCompanies(id, companyIds, version = null) {
    return apiClient.post(`/items/${id}/assign`, {
      company_ids: companyIds,
      version: version,
    })
  },

  /**
   * Unassign item from company
   * @param {number} id - Item ID
   * @param {number} companyId - Company ID
   * @returns {Promise}
   */
  async unassignFromCompany(id, companyId) {
    return apiClient.delete(`/items/${id}/assign/${companyId}`)
  },

  /**
   * Get download history for an item
   * @param {number} id - Item ID
   * @param {Object} pagination - Pagination options
   * @returns {Promise} - Download history
   */
  async getDownloadHistory(id, pagination = {}) {
    const params = new URLSearchParams()
    params.append('page', pagination.page || 1)
    params.append('limit', pagination.limit || 25)

    return apiClient.get(`/items/${id}/downloads?${params.toString()}`)
  },

  /**
   * Validate file before upload
   * @param {File} file - File to validate
   * @returns {Object} - Validation result
   */
  validateFile(file) {
    const errors = []
    const maxSize = 10 * 1024 * 1024 // 10MB
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ]
    const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx']

    if (!file) {
      errors.push('No file selected')
      return { valid: false, errors }
    }

    // Check file size
    if (file.size > maxSize) {
      errors.push('File size exceeds maximum limit of 10MB')
    }

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      const extension = file.name.split('.').pop()?.toLowerCase()
      if (!allowedExtensions.includes(extension)) {
        errors.push('Invalid file type. Allowed types: PDF, DOC, DOCX, XLS, XLSX')
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    }
  },

  /**
   * Get item types
   * @returns {Array} - Item type options
   */
  getItemTypes() {
    return [
      { value: 'policy', label: 'Policy', icon: 'FileText', color: '#3b82f6' },
      { value: 'procedure', label: 'Procedure', icon: 'List', color: '#10b981' },
      { value: 'template', label: 'Template', icon: 'FileTemplate', color: '#8b5cf6' },
      { value: 'guideline', label: 'Guideline', icon: 'BookOpen', color: '#f59e0b' },
      { value: 'resource', label: 'Resource', icon: 'Folder', color: '#6366f1' },
    ]
  },

  /**
   * Get status options
   * @returns {Array} - Status options
   */
  getStatusOptions() {
    return [
      { value: 'draft', label: 'Draft', color: '#6b7280' },
      { value: 'published', label: 'Published', color: '#10b981' },
      { value: 'archived', label: 'Archived', color: '#f59e0b' },
      { value: 'deleted', label: 'Deleted', color: '#ef4444' },
    ]
  },

  /**
   * Format file size
   * @param {number} bytes - File size in bytes
   * @returns {string} - Formatted file size
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  },

  /**
   * Get file icon based on mime type
   * @param {string} mimeType - File MIME type
   * @returns {string} - Icon name
   */
  getFileIcon(mimeType) {
    if (mimeType === 'application/pdf') return 'FileText'
    if (mimeType?.includes('word') || mimeType?.includes('wordprocessingml')) return 'FileType'
    if (mimeType?.includes('excel') || mimeType?.includes('spreadsheetml')) return 'FileSpreadsheet'
    return 'File'
  },
}

export default itemService
